import type { DialogStore } from '@ariakit/react'
import { type FC, type ReactElement, useState } from 'react'
import { ErrorBox } from '@/components/commons/error-box'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	DialogDisclosure,
	DialogDismiss,
	DialogFooter,
	DialogHeader,
	DialogHeading,
	DialogProvider
} from '@/components/ui/dialog-atoms'
import { AlertTriangleIcon, CheckIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Skeleton } from '@/components/ui/skeleton'
import {
	OpsVettingCheckIconContainer,
	OpsVettingChoosePhotoDialog,
	OpsVettingChoosePhotoDialogBody
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useTranslation } from '@/translations/i18n'
import { useGetExperienceVettingGalleryImages } from '../queries/use-get-experience-vetting-gallery-images'

const GalleryContent = ({
	isPending,
	isError,
	images,
	selectedImageId,
	onSelectImage
}: {
	isPending: boolean
	isError: boolean
	images: { id: number; url: string }[] | undefined
	selectedImageId: number | undefined
	onSelectImage: (id: number) => void
}) => {
	const { t } = useTranslation()
	if (isPending) {
		return Array.from({ length: 6 }, (_, i) => `skeleton-${i}`).map(
			(key) => (
				<Skeleton
					className='aspect-square h-full w-full rounded-lg object-cover'
					key={key}
				/>
			)
		)
	}
	if (isError) {
		return (
			<ErrorBox
				icon={<AlertTriangleIcon />}
				title={t('errors:default')}
			/>
		)
	}
	return images?.map((image) => (
		<GalleryImageItem
			key={image.id}
			onClick={() => onSelectImage(image.id)}
			src={image.url}
			topLeftSlot={
				selectedImageId === image.id && (
					<OpsVettingCheckIconContainer>
						<CheckIcon />
					</OpsVettingCheckIconContainer>
				)
			}
		/>
	))
}

interface ExpVettingCriteriaChoosePhotoDialogProps {
	onConfirm: (id: number) => void
	defaultSelectedImageId?: number
	experienceId: number
	galleryId: number
	trigger?: ReactElement
	store?: DialogStore
}

export const ExpVettingCriteriaChoosePhotoDialog: FC<
	ExpVettingCriteriaChoosePhotoDialogProps
> = ({
	onConfirm,
	defaultSelectedImageId,
	experienceId,
	galleryId,
	trigger,
	store
}) => {
	const [selectedImageId, setSelectedImageId] = useState(
		defaultSelectedImageId
	)
	const {
		data: images,
		isPending,
		isError
	} = useGetExperienceVettingGalleryImages({ experienceId, galleryId })
	const { t } = useTranslation()
	const disableConfirm =
		!selectedImageId || selectedImageId === defaultSelectedImageId

	return (
		<DialogProvider
			setOpen={() => setSelectedImageId(defaultSelectedImageId)}
			store={store}
		>
			{trigger && <DialogDisclosure render={trigger} />}
			<OpsVettingChoosePhotoDialog width='680px'>
				<DialogHeader>
					<DialogDismiss />
					<DialogHeading>{t('common:choose_a_photo')}</DialogHeading>
				</DialogHeader>
				<ScrollArea scrollbar={<Scrollbar />}>
					<OpsVettingChoosePhotoDialogBody>
						<GalleryContent
							images={images}
							isError={isError}
							isPending={isPending}
							onSelectImage={setSelectedImageId}
							selectedImageId={selectedImageId}
						/>
					</OpsVettingChoosePhotoDialogBody>
				</ScrollArea>
				<DialogFooter>
					<DialogDismiss
						render={
							<ButtonLegacy type='button' variant='tertiary' />
						}
					>
						{t('common:cancel')}
					</DialogDismiss>
					<DialogDismiss
						render={
							<ButtonLegacy
								disabled={disableConfirm}
								onClick={() =>
									selectedImageId &&
									onConfirm(selectedImageId)
								}
								type='button'
							/>
						}
					>
						{t('common:confirm')}
					</DialogDismiss>
				</DialogFooter>
			</OpsVettingChoosePhotoDialog>
		</DialogProvider>
	)
}
